import { useState, useRef, useId } from "react";
import { getCurrentProject } from "../sessionManager";
import { KnobHeadless, useKnobKeyboardControls } from 'react-knob-headless';
import throttle from 'lodash.throttle'
import { useDispatch, useSelector } from "react-redux";
import { FloatingValue } from "../components/FloatingValue";
import { useFocus } from "../hooks/useFocus";
import { lerp, polToCar, degToRad } from "../utils/math";
import { getWidgetData } from "../utils/parameters";

const ANGLE_MIN = -145;
const ANGLE_MAX = 145;
const MAX_MARKS = 17;
export const ParameterKnob = ({ parameterHandler, sideEffect, label, mida, colorizeLabel=false, markLabelRed=false, noOutput=false, knobSize, ...props }) => {
    const widgetData = getWidgetData(props['data-widget-id']);
    if (widgetData) label ??= widgetData.label?.ca;

    const [discreteOffset, setDiscreteOffset] = useState(0); // for when there are discrete options (parameterHandler.description.type === 'enum')
    const realValue = useSelector(parameterHandler.selector);

    const { num2Norm, norm2Num, real2Num, num2Real, real2String, getNumericMin, getNumericMax, getCardinality } = parameterHandler;
 
    const normValue = num2Norm(real2Num(realValue)); // without discreteOffset for snapping when there are discrete options
    const angle = normValue * (ANGLE_MAX - ANGLE_MIN) + ANGLE_MIN;
    
    const numValue = real2Num(realValue) + discreteOffset;
    const dispatch = useDispatch();

    const markQ = Math.min(getCardinality(), MAX_MARKS);
    const marks = <svg viewBox="-50 -50 100 100" className="absolute">
        <g stroke="currentColor" strokeWidth="1" vectorEffect="non-scaling-stroke">
            {Array(markQ).fill().map((_, i) => {
                const angle = lerp(ANGLE_MIN, ANGLE_MAX, i / (markQ - 1));
                const {x: x1, y: y1} = polToCar({r: 47.5, a: degToRad(angle - 90)});
                const {x: x2, y: y2} = polToCar({r: 42, a: degToRad(angle - 90)});
                return <line x1={x1} x2={x2} y1={y1} y2={y2}/>
            })
            }
        </g>
    </svg>

    const updateParameterThrottledFunction = useRef(throttle((newValue, parameterHandler, format) => {  // We use a ref to avoid creating a new function on each render which would reset throttle counter
        // We need to pass updateParameter together with newNumValue because there are cases in which the same knob changes the parametre that it controls at render time (basically in the samppler with the pad-dependant controls)
        if (format === "num") {
            const numValue = newValue;
            newValue = num2Real(numValue);
            format = "real";
            setDiscreteOffset(numValue - real2Num(newValue));
        }
        if (format !== "real") throw new Error("Format should be 'real' by now");
        dispatch(parameterHandler.updater(newValue));
        sideEffect && sideEffect();
    }, getCurrentProject().continuousControlThrottleTime));

    let labelClass = "";
    if (colorizeLabel) labelClass = "text-accent"
    if (markLabelRed) labelClass = "text-red"

    const { setRef, focus } = useFocus();

    const knobctrlId = useId();
    const { onKeyDown } = useKnobKeyboardControls({valueRaw: normValue, valueMin: 0, valueMax: 1, step: 0.01, stepLarger: 0.1, onValueRawChange: (newNormValue) => updateParameterThrottledFunction.current(norm2Num(newNormValue), parameterHandler) });
    return (
        <FloatingValue isVisible={focus && !noOutput} value={real2String(realValue)}>
            <div className={ `knob knob-${mida}` } {...props}>
                    <div className="knobctrl-wrapper" style={{width: knobSize, height: knobSize}}>
                        {marks}    
                        <KnobHeadless ref={setRef} id={knobctrlId} className="knobctrl" style={{rotate: `${angle}deg`}}
                            includeIntoTabOrder={true} onKeyDown={onKeyDown} // make it changeable via the keyboard
                            valueRaw={numValue}
                            valueMin={getNumericMin()}
                            valueMax={getNumericMax()}
                            mapTo01={(x) => num2Norm(x)}
                            mapFrom01={(x) => norm2Num(x)}
                            onValueRawChange={newNumValue => {
                                updateParameterThrottledFunction.current(newNumValue, parameterHandler, "num");
                            }}
                            valueRawRoundFn={(value)=>value.toFixed(2)}
                            valueRawDisplayFn={(numValue) => real2String(num2Real(numValue))}
                            dragSensitivity="0.009"
                            axis='xy'
                            onDoubleClick={() => updateParameterThrottledFunction.current(parameterHandler.getDefaultValue(), parameterHandler, "real")}
                        />
                    </div>
                    <label htmlFor={knobctrlId} className={labelClass}>{label || parameterHandler.description.label}</label>
            </div>
        </FloatingValue>
    )
};